import { Tooltip } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, Fragment, MouseEvent, useCallback, useMemo } from 'react';
import { PinboardCard } from '../../PinboardCard/PinboardCard';
import { CardType } from '../../TimetableVersion/graphql/types';
import { useTimetableStore } from '../TimetableProvider';
import styles from './CardPicker.module.scss';

type CardPickerProps = {
  searchString: string;
};

export const CardPicker: FC<CardPickerProps> = observer(({ searchString }: CardPickerProps) => {
  const pinboardStore = useTimetableStore();

  const freeCards = pinboardStore.freeCardsSet;

  function handleClick(e: MouseEvent) {
    e.stopPropagation();
    const pickedCard = pinboardStore.pickedCard;
    if (pickedCard) {
      if (pinboardStore.context === 'rooms') {
        pinboardStore.discardPickedCardToRoomsStack();
      } else {
        pinboardStore.discardPickedCardToStack();
      }
    }
  }
  const cardsStack = pinboardStore.getCardsStack();

  const cardsArray = useMemo(() => {
    return cardsStack ? Array.from(cardsStack.values()) : [];
  }, [cardsStack]);

  const filteredCards = useMemo(() => {
    return cardsArray.filter((card) => {
      const subject = card[0].subject?.shortName ?? '';
      return searchString === '' || subject.toLowerCase().includes(searchString.toLowerCase());
    });
  }, [cardsArray, searchString]);

  // sort by subject-name
  const stacks: CardType[][] = filteredCards.sort((a, b) =>
    (a[0].subject?.shortName ?? '').localeCompare(b[0].subject?.shortName ?? ''),
  );

  // Gesteckte Position (Wochentag + Name der Startstunde) der Karte – im Raum-Kontext als Tooltip.
  const positionHint = (card: CardType): string | undefined => {
    if (pinboardStore.context !== 'rooms') return undefined;
    const day = pinboardStore.days.find((d) => d.value === card.weekday)?.label;
    const time = pinboardStore.timeGridEntries?.find((e) => e.uuid === card.startTimeGridEntry?.uuid)?.name;
    return [day, time].filter(Boolean).join(', ') || undefined;
  };

  const onCardClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, card: CardType) => {
      event.stopPropagation();
      event.preventDefault();
      if (pinboardStore.pickedCard) {
        pinboardStore.discardPickedCard();
      }
      pinboardStore.pickCard(
        {
          cardUuid: card.uuid,
          cardRows: card.badgeCardRows,
          duration: card.duration ?? 1,
          counter: 1,
          fromStack: true,
          label: card.subject?.shortName ?? '',
          labelColor: card.badgeCardTextColor,
          position: { x: event.clientX, y: event.clientY },
        },
        undefined,
        true,
        event.shiftKey,
      );
    },
    [pinboardStore],
  );

  return (
    <div className={classNames(styles['card-picker'], 'has-scrollbar')} onClick={(e) => handleClick(e)}>
      {freeCards.size > 0 &&
        stacks.map((stack) => {
          const card = stack[0];
          const count = stack.length;
          const cardClasses = classNames({
            [styles.multi]: stack.length >= 2,
          });
          const hint = positionHint(card);

          const cardEl = (
            <PinboardCard className={cardClasses} card={card} count={count} hideTitle={!!hint} onClick={onCardClick} />
          );

          // Tooltip (ui-components) ist "teuer" → nur im Raum-Kontext rendern.
          return hint ? (
            <Tooltip key={card.uuid} content={hint}>
              {cardEl}
            </Tooltip>
          ) : (
            <Fragment key={card.uuid}>{cardEl}</Fragment>
          );
        })}
    </div>
  );
});
